import { useEffect, useState, type FormEvent } from 'react'
import { Warning as AlertTriangle } from '@phosphor-icons/react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'

type Props = {
  open: boolean
  onClose: () => void
  beneficiarioId: string
  beneficiarioNombre: string
  porcentaje: number
  onSuccess: () => void
}

export function RevocarBeneficiarioModal({
  open, onClose, beneficiarioId, beneficiarioNombre, porcentaje, onSuccess,
}: Props) {
  const [motivo, setMotivo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setMotivo('')
    setError(null)
  }, [open])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error: rpcErr } = await supabase.rpc('revocar_beneficiario', {
      p_beneficiario_id: beneficiarioId,
      p_motivo: motivo.trim() || null,
    })
    setSubmitting(false)
    if (rpcErr) {
      setError(`No se pudo revocar: ${rpcErr.message}`)
      return
    }
    onSuccess()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Revocar beneficiario"
      subtitle={`${beneficiarioNombre} — ${porcentaje.toFixed(0)}%`}
      size="sm"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="submit"
            form="revocar-form"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Revocando…' : 'Revocar beneficiario'}
          </button>
        </>
      }
    >
      <form id="revocar-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        <div
          style={{
            display: 'flex',
            gap: 10,
            padding: '12px 14px',
            background: '#fffbeb',
            border: '1px solid rgba(217, 119, 6, 0.25)',
            borderRadius: 10,
            color: '#78350f',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <AlertTriangle size={16} weight="bold" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            El beneficiario deja de estar vigente. El registro queda en el histórico
            y libera el {porcentaje.toFixed(0)}% para reasignar.
          </span>
        </div>

        <label className="form-field">
          <span className="form-label">Motivo (opcional)</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej. Solicitud del asociado por cambio en su designación."
            rows={3}
            style={{ resize: 'vertical', minHeight: 70 }}
          />
        </label>
      </form>
    </Modal>
  )
}
